// Saying that a seed is a remix, and whose.
//
// Three pieces, because six surfaces need the same sentence at three weights
// and one of them must not contain links: FeedRow and MobileChartRow put their
// meta text INSIDE a <Link> to the seed, and an anchor inside an anchor is
// invalid DOM that React warns about.

import { Link } from "react-router";
import { GitBranch, Pause, Play } from "lucide-react";
import type { RemixOrigin } from "@shared/api-types.js";
import { remixCreditText } from "@/lib/lineage";
import { profilePath, seedPath } from "@/lib/ctas";
import { cn } from "@web-shared/lib/utils";
import { usePlayer, useTrackStatus, type PlayerTrack } from "../player";
import { Avatar } from "./Avatar";

/** The original's track, ready to queue ahead of the remix. */
function originTrack(origin: RemixOrigin): PlayerTrack {
  return {
    id: origin.id,
    title: origin.title,
    url: origin.audioUrl,
    producer: {
      handle: origin.producer.handle ?? origin.producer.id,
      username: origin.producer.username,
    },
    href: seedPath(origin.id),
    countsFor: origin.id,
  };
}

/**
 * Play the seed this grew from, then the remix itself — hearing the two back
 * to back says more about the lineage than any label.
 *
 * Renders nothing when the original has no master: words without a ▶ beat a
 * button that does nothing.
 */
export function OriginalButton({
  origin,
  then,
  variant = "chip",
  className,
}: {
  origin: RemixOrigin;
  then: PlayerTrack | null;
  /** "chip": the feed footer's 10px pill. "cta": a real touch target. */
  variant?: "chip" | "cta";
  className?: string;
}) {
  const player = usePlayer();
  const status = useTrackStatus(origin.id);
  if (!origin.audioUrl) return null;
  const track = originTrack(origin);
  const playing = status === "playing";
  return (
    <button
      type="button"
      onClick={() => player.toggle(track, then ? [track, then] : [track])}
      className={cn(
        "shrink-0 cursor-pointer rounded-full border transition-colors",
        variant === "chip"
          ? "px-2 text-[10px] leading-5"
          : "inline-flex h-9 items-center gap-1.5 px-3 text-xs font-semibold",
        playing || status
          ? "border-primary text-primary"
          : "border-border text-muted-foreground hover:text-foreground",
        className,
      )}
      title={`Play ${origin.title} by ${origin.producer.username}, then this remix`}
      aria-label={`Play the original, ${remixCreditText(origin)}`}
    >
      {variant === "chip" ? (
        playing ? "❚❚ original" : "▶ original"
      ) : (
        <>
          {playing ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
          Hear the original
        </>
      )}
    </button>
  );
}

/**
 * The one-liner, for cards and rows.
 *
 * `linked={false}` where the caller already wraps this in a <Link>. It is not
 * cosmetic — nested anchors are invalid.
 */
export function RemixByline({
  origin,
  variant = "full",
  linked = true,
  className,
}: {
  origin: RemixOrigin;
  /** "full": of X by Y. "producer-only": by Y (a narrow card). */
  variant?: "full" | "producer-only";
  linked?: boolean;
  className?: string;
}) {
  const title = linked ? (
    <Link to={seedPath(origin.id)} className="text-foreground hover:underline">
      {origin.title}
    </Link>
  ) : (
    <span className="text-foreground">{origin.title}</span>
  );
  const producer = linked ? (
    <Link to={profilePath(origin.producer.handle ?? origin.producer.id)} className="text-foreground hover:underline">
      {origin.producer.username}
    </Link>
  ) : (
    <span className="text-foreground">{origin.producer.username}</span>
  );
  return (
    <span
      className={cn("inline-flex min-w-0 items-center gap-1 text-muted-foreground", className)}
      title={remixCreditText(origin)}
    >
      <GitBranch className="size-3 shrink-0 text-primary/80" aria-hidden />
      <span className="truncate">
        {variant === "full" ? <>remix of {title} by {producer}</> : <>remix of {producer}</>}
      </span>
    </span>
  );
}

/**
 * The seed page's attribution block: who to credit, what changed, and the
 * original a tap away. Sits under the title on both layouts — a remix that
 * needs scrolling to be a remix is the thing this whole change is fixing.
 */
export function RemixCredit({
  origin,
  self,
  note,
  size = "md",
  className,
}: {
  origin: RemixOrigin;
  /** The remix itself, queued after the original. */
  self: PlayerTrack | null;
  note: string | null;
  /** "line": the desktop hero's — one unboxed line, the original beside it. */
  size?: "md" | "sm" | "line";
  className?: string;
}) {
  const small = size === "sm";
  if (size === "line") {
    return (
      <p className={cn("flex min-w-0 items-center gap-2 text-sm text-muted-foreground", className)}>
        <GitBranch className="size-3.5 shrink-0 text-primary/80" aria-hidden />
        <span className="min-w-0 truncate" title={remixCreditText(origin)}>
          Remix of{" "}
          <Link to={seedPath(origin.id)} className="font-medium text-foreground hover:underline">
            {origin.title}
          </Link>{" "}
          by{" "}
          <Link to={profilePath(origin.producer.handle ?? origin.producer.id)} className="font-medium text-foreground hover:underline">
            {origin.producer.username}
          </Link>
        </span>
        <OriginalButton origin={origin} then={self} />
      </p>
    );
  }
  return (
    <div
      className={cn(
        "rounded-xl border border-primary/25 bg-primary/5 px-3 py-2",
        small ? "flex flex-col items-center gap-2 text-center" : "flex flex-col gap-2",
        className,
      )}
    >
      <p
        className={cn(
          "flex flex-wrap items-center gap-x-2 gap-y-1",
          small ? "justify-center text-xs" : "text-sm",
        )}
      >
        <Link
          to={profilePath(origin.producer.handle ?? origin.producer.id)}
          className="shrink-0"
          aria-hidden
          tabIndex={-1}
        >
          <Avatar name={origin.producer.username} url={origin.producer.avatarUrl} className="size-6" />
        </Link>
        <span className="text-muted-foreground">
          Remix of{" "}
          <Link to={seedPath(origin.id)} className="font-medium text-foreground hover:underline">
            {origin.title}
          </Link>{" "}
          by{" "}
          <Link
            to={profilePath(origin.producer.handle ?? origin.producer.id)}
            className="font-medium text-foreground hover:underline"
          >
            {origin.producer.username}
          </Link>
        </span>
        {!small && <OriginalButton origin={origin} then={self} className="ml-auto" />}
      </p>
      {small && <OriginalButton origin={origin} then={self} variant="cta" />}
      {note && (
        <p
          className={cn(
            "italic text-muted-foreground",
            small ? "line-clamp-3 max-w-[320px] text-xs" : "text-sm",
          )}
        >
          &ldquo;{note}&rdquo;
        </p>
      )}
    </div>
  );
}
